import { useState, useRef, useEffect } from "react";
import { useMatter } from "../contexts/MatterContext";
import { Field, Label } from "../design/fieldset";
import { Checkbox } from "../design/checkbox";
import { User } from "../models/User";

interface AssignToFieldProps {
  value: User["_id"][];
  onChange: (selectedContacts: User["_id"][]) => void;
  disabled?: boolean;
}

const AssignToField: React.FC<AssignToFieldProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { currentMatter } = useMatter();
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    if (!disabled) setShowDropdown((prev) => !prev);
  };

  const toggleContact = (id: string) => {
    const isSelected = value.includes(id);
    const newSelection = isSelected
      ? value.filter((contactId) => contactId !== id)
      : [...value, id];
    onChange(newSelection);
  };

  const selectedNames = (currentMatter?.users ?? [])
    .filter((user) => value.includes(user._id))
    .map((user) => user.name)
    .join(", ");

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  return (
    <Field ref={containerRef}>
      <Label>Assign To</Label>
      <div
        className={`bg-white dark:bg-zinc-900 rounded cursor-pointer ${
          disabled ? "opacity-50 pointer-events-none" : ""
        }`}
        onClick={toggleDropdown}
      >
        <div className="flex items-center justify-between px-4 py-3 border rounded">
          {selectedNames || "Select owners..."}
        </div>
        {showDropdown && (
          <div className="z-20 w-full overflow-auto bg-white rounded shadow-md dark:bg-zinc-900 max-h-60">
            {(currentMatter?.users ?? []).map((user) => {
              const selected = value.includes(user._id);
              return (
                <div
                  key={user._id}
                  onClick={() => toggleContact(user._id)}
                  className={`flex border rounded items-center justify-between gap-2 px-4 py-3 cursor-pointer transition  
                  ${
                    selected
                      ? "bg-blue-50 border-blue-500 dark:bg-zinc-800"
                      : "hover:bg-gray-50 dark:hover:bg-zinc-800"
                  }`}
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-zinc-800 dark:text-zinc-100">
                      {user.name}
                    </span>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">
                      {user.email}
                    </span>
                  </div>
                  <Checkbox checked={selected} disabled />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Field>
  );
};

export default AssignToField;
